import { z } from "zod";
import { CONFLICT_MSG } from "./concurrent.js";
import { InstallmentDefault, InstallmentDefaults } from "./installment.js";
import { taxKindOf } from "./tax.js";

// ---------------------------------------------------------------------------
// PC の見積書の画面（#131、docs/47 §12、docs/10 §9.1）。Figma「見積（スマホ・PC）10/3」PC1・D1〜D3。
//   ・画面に出す文（docs/10 §5.1.2：簡潔に・業務の言葉で）
//   ・仕入れの集計（「仕入れ未入力 n行」。画面で全行を数えない＝API が返す）
//   ・積算表を付けるか（見積ごとの選択。空なら会社の設定）
//   ・丸ごとの保存に版を付けたときの「読み直し」：ほかで変わった分と、この画面で直した分を合わせる（rebaseEdits）
// ---------------------------------------------------------------------------

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);

export const ESTIMATE_PC_MSG = {
  /** 版がずれた（PUT /estimates/:id の 409 ESTIMATE_STALE）。文は CONFLICT_MSG の1か所（#202） */
  staleAsk: CONFLICT_MSG.ask,
  staleNote: CONFLICT_MSG.note,
  staleReload: CONFLICT_MSG.reload,
  staleLater: CONFLICT_MSG.later,
  staleSaving: CONFLICT_MSG.saving,
  staleMerged: CONFLICT_MSG.merged,
  staleReloaded: CONFLICT_MSG.reloaded,
  /** 頭の帯（#131 の2回目）。No. は発行の瞬間に採番（#187 で「未採番」から＝業務の言葉。一覧の No. と同じ） */
  noUntilIssue: "未発行",
  totalLabel: "合計（税込）",
  /** 「詳細」の中の{customer}担当者（#187。前は2行目の「担当者 なし ⌄」） */
  contact: "担当者",
  contactNone: "なし",
  contactAdd: "担当者新規登録",
  branch: "支店・営業所",
  branchNone: "本社",
  /** {project}の行の右（#187） */
  openGroup: "{project}表示",
  /** 工務店向けの言葉「工事場所」のときの短い名前（ほかの言葉はそのまま） */
  site: "現場",
  validity: "有効期限",
  staff: "担当",
  /** 頭の帯の2行目の右（#138：縁付きの小さなボタン。右に ⌄／⌃。#187 で 表示順・↶ ↷・社内用 の右） */
  more: "詳細",
  /** 外注見積の帯（D1） */
  vendorCount: "{vendorQuote} {n}件",
  /** #274（docs/99 §4、10/31 射場さん ★3）：文（〜ました）と「残り」をやめ、列の見出しと同じ名詞に（取込済／未取込／全行取込済） */
  vendorDone: "取込済 {yen}円",
  vendorRest: "未取込 {n}行 {yen}円",
  vendorAllDone: "全行取込済",
  vendorRegister: "{vendorQuote}登録",
  vendorOpen: "一覧",
  /** #140：「一覧を閉じる」→「閉じる」（「一覧」と同じ幅のボタン） */
  vendorClose: "閉じる",
  /** 列の見出し＝言葉 {vendor}（現場力＝外注先・営業力＝仕入先。画面は t() を通す。10/31 に営業力でも「外注先」と出ていたのを直した） */
  vendorColName: "{vendor}",
  vendorColState: "状態",
  vendorColDone: "取込済",
  vendorColRest: "未取込",
  /** 取込の1枚の「未取込の行を全部選ぶ」ボタン（#274：前は「残り選択」） */
  vendorPickRest: "未取込選択",
  vendorColSale: "売価 ／ 仕入れ",
  vendorOriginal: "原本",
  vendorImport: "取込",
  requestedState: "未送付",
  requestedRest: "{n}行を依頼",
  /** 道具の列（#131 の2回目。#187 から内訳のタブと紙の形だけ＝表示順・社内用は頭の帯の2行目） */
  oneSheet: "1枚",
  coverSheets: "表紙＋内訳",
  showInternal: "社内用",
  sheetMenu: "{name}のほかの動き",
  sheetRename: "名前",
  sheetLeft: "左へ",
  sheetRight: "右へ",
  sheetLabor: "労務費",
  sheetDelete: "削除",
  /** #199（docs/80）：内訳の複製と不使用。表紙のタブには出さない */
  sheetCopy: "複製",
  sheetDisuse: "利用しない",
  sheetUse: "利用する",
  /** 不使用の内訳のタブ・表紙の行の右の小さな印 */
  sheetUnused: "不使用",
  /** 総額指定の値引きの行は1見積に1つなので、複製に写さない（通知） */
  sheetCopyNoTarget: "総額指定の値引きは複製しません",
  /** POST /estimates/:id/sheets/:sheetId/copy の断り */
  sheetCopyCover: "表紙は複製できません",
  sheetNotFound: "内訳が見つかりません",
  /** 選んだ行の複製（#199）：まとめて操作の帯のボタンと通知。写した行は選んだ行の直後 */
  rowsCopy: "複製",
  rowsCopied: "{n}行を複製しました",
  /** 率の行の基礎（選んだ小計・選んだ行・労務費）が不使用の内訳にあるとき（基礎は 0） */
  rateBaseUnused: "対象の行は不使用の内訳にあります",
  /** 内訳をやめて1枚にまとめるときの確認（不使用の内訳があるときだけ） */
  mergeUnusedTitle: "1枚にまとめる",
  mergeUnusedAsk: "不使用の内訳「{names}」は「削除した内訳」に移ります（30日以内なら復元できます）",
  mergeUnusedOk: "決定",
  /** 内訳の名前の窓（#138：「内訳の名前を変える」→「内訳名称」） */
  sheetRenameTitle: "内訳名称",
  addSheet: "内訳追加",
  sortLocked: "表示順を「手動」にすると並べ替えられます",
  dragHandle: "ドラッグで並べ替え",
  /** 元に戻す／やり直す（#140。道具の列の ↶ ↷、⌘Z／⌘⇧Z） */
  undo: "元に戻す",
  redo: "やり直す",
  /** 表 */
  internalHead: "社内用（{customer}には出しません）",
  /** #140：社内用の見出しの段はやめて「仕入元」の右の「？」に（文は internalHead） */
  internalHelpLabel: "社内用の列の説明",
  /** 規格・仕様／備考 の幅（#140）：見出しの境目のドラッグと「…」 */
  colWidth: "{name}の幅",
  colWidthMenu: "{name}の幅",
  colNarrow: "狭い",
  colNormal: "標準",
  colWide: "広い",
  colReset: "既定幅に復元",
  colCost: "仕入れ",
  colMargin: "利益率",
  /** 社内用の3つ目の列（#138：「出どころ」→「仕入元」） */
  colSource: "仕入元",
  costMissing: "未入力",
  costMissingCount: "仕入れ未入力 {n}行",
  under: "原価割れ",
  manual: "手入力",
  own: "自社",
  /** 仕入元の欄の％計算の行（#314：区分の名前と同じ） */
  rateRow: "％計算",
  requested: "未送付",
  photos: "写真 {n}枚",
  addLine: "行追加",
  otherLines: "ほかの区分",
  /** お知らせの1行（該当があるときだけチップ） */
  provisionalCount: "仮 {n}行",
  finalizeAll: "すべて確定",
  finalizedAll: "{n}行を確定にしました",
  lumpsumCount: "一式（内訳なし）{n}行",
  underCount: "原価割れ {n}行",
  tradeMissing: "{trade}なし {n}行",
  placeMissing: "場所なし {n}行",
  fillIn: "入力",
  /** #188：{axis} に入る言葉（{trade}・場所）は画面が t() を通してから埋める（前は「{trade}を入れる」がそのまま出た） */
  fillTitle: "{axis}を入力",
  fillDone: "{n}行に{axis}を入力しました",
  /** 行の詳細（1行） */
  rowKind: "区分",
  /** 率の行の2段目（#306・#309、docs/103 §2.2）：費目 → 率 → 対象。対象は文字のボタン「対象 労務費 30,000円 ▾」とポップ */
  costKind: "費目",
  rate: "率",
  rateTarget: "対象",
  rateTargetMenu: "対象選択",
  /** 対象の金額が 0 円のとき（朱の文字。色だけにしない） */
  rateTargetEmpty: "対象の行がありません",
  rateNoSubtotal: "小計がありません",
  rateNoDetail: "明細がありません",
  /** 値引きの行の2段目（#309：「総合計（税抜）を ○ 円にする」から） */
  discountTarget: "税抜合計",
  discountTargetTail: "円に調整",
  discountTargetView: "税抜合計を {yen}円に調整",
  /** スマホで作った仮の行のメモ（#309：「仮のメモ」から） */
  provisionalNote: "仮メモ",
  /** 一括変更の帯の「区分」（#305。明細と労務費だけ・field の会社だけ） */
  bulkKind: "区分",
  addBelow: "下に追加",
  /** #302（C1）：行の ⋮（No の左のつまみ）の1段目「上に追加」「下に追加」 */
  addAbove: "上に追加",
  deleteRow: "削除",
  /** 行の詳細の × のボタンの読み上げ（#138：文字をやめて × のアイコン） */
  closeRow: "閉じる",
  /** 行の削除の窓（#140）：何か入っている行だけ。空の行は窓なしで消す */
  rowDeleteTitle: "行削除",
  rowDeleteAsk: "「{name}」を削除しますか",
  rowDeleteAskBlank: "この行を削除しますか",
  /** 拾い出しの合計の数量に乗せたときの文（#190：巻尺のしるしをやめ、数字の下の点線に。数は「行」） */
  takeoffMark: "拾い出し {n}行",
  /** 行の詳細の「拾い出し n行 ›」 */
  takeoffCount: "{n}行",
  noAxis: "—",
  moveUp: "上へ",
  moveDown: "下へ",
  gripMenu: "{name}の並べ替え",
  lumpsum: "一式",
  /** 行の「…」（D2） */
  rowMenu: "{name}のほかの動き",
  menuImport: "{vendorQuote}から取込",
  /** #312：「外注」の字を書かない（項目の名前が{vendorQuote}） */
  menuImportCount: "{n}件",
  menuCostManual: "仕入れ手入力",
  menuCostOwn: "自社に変更",
  menuPhotos: "写真表示",
  menuPhotosCount: "{n}枚",
  menuUnlink: "{vendorQuote}連携解除",
  menuFinalize: "確定",
  /** 内訳へ移動（#138）：行の「…」のサブメニュー・まとめて選んだ帯のプルダウン。移す先は 表紙＋内訳（いまの紙は出さない） */
  menuMoveSheet: "内訳へ移動",
  moveSheetMenu: "{name}を移す内訳",
  moveCover: "表紙",
  moveDone: "{n}行を {name} へ移動",
  /** 一式の行を移すと、その内訳の行（子の行）も一緒に動く。子の行だけを選んだときは親の一式ごと動かす */
  moveWithChildren: "（一式の内訳を含む）",
  /** まとめて選んだ中の 見出し・小計・率・値引き は動かさない */
  moveSkipped: "（見出し・小計・率・値引きの {n}行は対象外）",
  moveNone: "移動できる行がありません（明細・説明・別途だけ）",
  /** {vendor}の取引先が無いとき（見積依頼・{order}の窓） */
  requestNoVendor: "外注先の取引先がありません。{customer}の画面で「外注先」の取引先を登録してください",
  ownDone: "「{name}」の仕入れを自社にしました",
  /** 行の「…」の「見積依頼解除」（#270、docs/88 §4.5）のあとの通知 */
  requestCleared: "「{name}」の見積依頼を外しました",
  /** 見出しの右の「⋯」（#173：前の「≡」と「⋯」を1つに。状態の操作／PDF・見積依頼／削除 の3段。下書きにも出る） */
  headMenu: "見積書のほかの動き",
  /** 下書きの紙を見る（#149：見積書・請求書・領収書・見積依頼書で同じ文言） */
  pdfDraft: "PDF で確認（透かし入り）",
  /** #302（B1）：下書きの見積書・請求書の頭の帯の脇のボタン（発行の左）。乗せると pdfDraft */
  pdfCheck: "PDF 確認",
  /** #302（B3）：出した回の行の「…」の仕入元（社内用の列は発行後でも直せる） */
  menuVendorChange: "仕入元変更",
  menuVendorClear: "仕入元解除",
  vendorChanged: "仕入元を変更しました。",
  vendorCleared: "仕入元を外しました。",
  pdfOpen: "PDF 表示",
  /** #302（C3）：見積書の頭の帯の「受注」（発行済で未受注のとき。一覧の受注と同じ動き）・「失注」は ⋯ の中 */
  decideOrdered: "受注",
  decideLost: "失注",
  formSheets: "PDF：表紙＋内訳書",
  formFlat: "PDF：1枚",
  /** #142 段6 No.29：言葉の {takeoffSheet}（積算表／数量の内訳表） */
  takeoffSheet: "{takeoffSheet}添付（別紙）",
  takeoffSheetOn: "{takeoffSheet}を付けます（別紙）",
  takeoffSheetOff: "{takeoffSheet}を付けません",
  discard: "下書き削除",
  /** 出す前の確認 */
  provisionalWarn: "仮の行が {n}本あります。",
  /** 備考の欄 */
  memoLabel: "備考",
  /** 差替文の欄（035 の replace_sentence。2回目からの回を直しているときだけ。#188 で「差し替えの一文（…）」から。説明は「？」） */
  replaceSentence: "差替文",
  replaceSentenceHelp: "備考の先頭に印字します。空にすると印字しません。",
  /** 回の帯の中の切替（#188。前は表の上の段「前の回とのちがい」「変わった行だけ」）。#190 から読み上げの名前（見える名前は marksLabel） */
  marksShow: "相違内容表示",
  marksOnly: "変更行のみ表示",
  /** #190：スイッチ2つを3択のセグメントに。見える名前は「相違」、既定は「なし」 */
  marksLabel: "相違",
  marksModes: { none: "なし", show: "表示", only: "変更行のみ" },
  /** 変更行のみで、この紙に変更行が無いとき */
  marksNoneHere: "この内訳に変更行はありません。",
  /** 設定 → 見積（#131 の2回目） */
  /** 設定 → 見積・請求（#142 段3 で「見積」から。分割の既定を足した。#154 で分割の既定は下の画面 /settings/estimate/installments、docs/63 §3） */
  settingsTitle: "見積・請求",
  installmentsTitle: "分割の既定",
  installmentsHelp: "新しく作る分割の予定の下書きに入ります。今ある分割の予定は変わりません。最後の回は残りです。",
  /** 下の画面の節「回」と注 */
  installmentsSection: "回",
  installmentsNote: "最後の回は残りです。今ある分割の予定は変わりません",
  installmentsRound: "{n}回目",
  installmentsName: "名前",
  installmentsPercent: "割合（%）",
  installmentsRest: "残り",
  installmentsAdd: "回追加",
  installmentsRemove: "{n}削除",
  installmentsReset: "既定に復元",
  /** 製品の既定のまま（題の下の注） */
  installmentsIsProduct: "製品の既定です",
  installmentsResetDone: "分割の既定を元に戻しました",
  costDefaultLabel: "新しい行の仕入れ",
  costDefaultSaved: "新しい行の仕入れを「{v}」にしました",
} as const;

/** 新しい行の仕入れの出どころの既定（#131 の2回目、tenants.default_cost_source）：own＝自社（仕入れ 0）／none＝未定（「仕入れ未入力」に数える） */
export const CostDefault = z.enum(["own", "none"]);
export type CostDefault = z.infer<typeof CostDefault>;
export const COST_DEFAULT_LABEL: Record<CostDefault, string> = { own: "自社", none: "未定" };
/**
 * GET /company/estimate-settings（設定 → 見積・請求。読むのはだれでも）。
 *   installments＝会社の「分割の既定」（#142 段3。null＝製品の既定）、installmentsProduct＝製品の既定（画面で「既定に戻す」と見せる）
 */
export const EstimateSettings = z.object({
  defaultCostSource: CostDefault,
  installments: InstallmentDefaults.nullable().default(null),
  installmentsProduct: z.array(InstallmentDefault).default([]),
  /** 締め請求書の明細の形の既定（#267 ★3）：perNote＝1枚1行（既定）／lines＝全行 */
  closingMode: z.enum(["lines", "perNote"]).default("perNote"),
  /** 定期請求（#268）：下書きの自動作成（既定オン）・作成時期（請求日の n日前。既定 7） */
  recurringAuto: z.boolean().default(true),
  recurringLeadDays: z.number().int().min(1).max(28).default(7),
});
export type EstimateSettings = z.infer<typeof EstimateSettings>;
/** PUT /company/estimate-settings（管理者）。省いた欄は変えない。installments: null で製品の既定に戻す */
export const EstimateSettingsUpdate = z.object({
  defaultCostSource: CostDefault.optional(),
  installments: InstallmentDefaults.nullable().optional(),
  closingMode: z.enum(["lines", "perNote"]).optional(),
  recurringAuto: z.boolean().optional(),
  recurringLeadDays: z.number().int().min(1).max(28).optional(),
});
export type EstimateSettingsUpdate = z.infer<typeof EstimateSettingsUpdate>;

/** 仕入れの集計（社内用の小計の行）。ふつうの行だけを数える（率・別途・値引き・見出し・文章は仕入れを持たない）。ルール7：未入力は 0 で埋めない */
export const CostSummary = z.object({
  /** 仕入れを入れた行の和（税抜） */
  total: Yen,
  /** ふつうの行で仕入れが未入力の行の数（> 0 のあいだ合計の利益率は出さない） */
  missing: z.number().int(),
  /** ふつうの行の数（立替金の行は数えない＝T2） */
  lines: z.number().int(),
});
export type CostSummary = z.infer<typeof CostSummary>;

/** 仕入れの欄を持つ行か：ふつうの行で、立替金でない（T2：立替金は仕入れ・粗利に入れない＝未入力に数えない） */
export const hasCostField = (l: { role: string; taxCategory?: string | null | undefined }): boolean =>
  l.role === "detail" && !(l.taxCategory && taxKindOf(l.taxCategory) === "reimbursement");

export function costSummaryOf(lines: ReadonlyArray<{ role: string; costAmount: number | null; costOwn?: boolean; taxCategory?: string | null | undefined }>): CostSummary {
  const ds = lines.filter(hasCostField);
  return {
    // 自社の行（#131 の2回目）は仕入れ 0 として数える＝未入力に入れない
    total: ds.reduce((a, l) => a + (l.costOwn ? 0 : (l.costAmount ?? 0)), 0),
    missing: ds.filter((l) => !l.costOwn && l.costAmount == null).length,
    lines: ds.length,
  };
}

/** 利益率（％・小数1桁）：（金額 − 仕入れ）÷ 金額。金額が 0 以下・仕入れが未入力なら null（分からないものを数字で埋めない） */
export function marginPctOf(amount: number, cost: number | null): number | null {
  if (cost == null || amount <= 0) return null;
  return Math.round(((amount - cost) / amount) * 1000) / 10;
}

/** 積算表を付けるか（#131）：見積ごとの選択（documents.print_takeoff_sheet）。null は会社の設定に従う */
export const TakeoffSheetInfo = z.object({
  on: z.boolean(),
  /** estimate＝この見積で選んだ／company＝会社の設定（設定 → 自社情報） */
  source: z.enum(["estimate", "company"]),
});
export type TakeoffSheetInfo = z.infer<typeof TakeoffSheetInfo>;

/** PUT /estimates/:id/takeoff-sheet。null で会社の設定に戻す */
export const TakeoffSheetUpdate = z.object({
  on: z.boolean().nullable(),
  /** 版（#202）：Estimate.updatedAt。違えば 409 ESTIMATE_STALE。省けば見ない。書いたら版を進める */
  version: z.string().min(1).max(40).optional(),
});
export type TakeoffSheetUpdate = z.infer<typeof TakeoffSheetUpdate>;

// ---------------------------------------------------------------------------
// 読み直し（#131）：丸ごとの保存が 409 で断られたとき、ほかで変わった最新（theirs）に、
// この画面で直した分（base → mine の差）を重ねる。行・紙は id で突き合わせ、欄ごとに「この画面で変えた欄だけ」上書きする。
//   ・この画面で足した行：mine の並びで1つ前の行のあとへ
//   ・ほかで足した行（スマホの仮の行など）：theirs の並びで1つ前の行のあとへ（消さない）
//   ・この画面で消した行：消す／ほかで消した行：この画面で直していなければ消す（直していれば残す）
//   ・並び：この画面の並び（mine）を基に、ほかで足した行を差し込む
// ---------------------------------------------------------------------------

type WithId = { id: string };
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** 欄ごとに：base と mine で違う欄は mine、同じ欄は theirs */
export function rebaseFields<T extends object>(base: T, mine: T, theirs: T): T {
  const out = { ...theirs } as Record<string, unknown>;
  const b = base as Record<string, unknown>;
  const m = mine as Record<string, unknown>;
  for (const k of new Set([...Object.keys(b), ...Object.keys(m)])) {
    if (!same(b[k], m[k])) out[k] = m[k];
  }
  return out as T;
}

/** id のある並び（行・紙）を重ねる */
export function rebaseList<T extends WithId>(base: readonly T[], mine: readonly T[], theirs: readonly T[]): T[] {
  const B = new Map(base.map((x) => [x.id, x]));
  const T_ = new Map(theirs.map((x) => [x.id, x]));
  const out: T[] = [];
  for (const m of mine) {
    const b = B.get(m.id);
    const t = T_.get(m.id);
    if (!b) {
      out.push(m); // この画面で足した
    } else if (t) {
      out.push(rebaseFields(b, m, t)); // 両方にある：この画面で変えた欄だけ上書き
    } else if (!same(b, m)) {
      out.push(m); // ほかで消したが、この画面で直していた → 残す
    }
    // ほかで消して、この画面では直していない → 消す
  }
  // ほかで足した行（base にも mine にも無い）：theirs の1つ前の行のあとへ
  const minesIds = new Set(mine.map((x) => x.id));
  theirs.forEach((t, i) => {
    if (B.has(t.id) || minesIds.has(t.id)) return;
    let at = 0;
    for (let k = i - 1; k >= 0; k--) {
      const j = out.findIndex((x) => x.id === theirs[k]!.id);
      if (j >= 0) {
        at = j + 1;
        break;
      }
    }
    out.splice(at, 0, t);
  });
  return out;
}

/**
 * 見出し（頭の欄）・行・紙をまとめて重ねる。行が指す紙が無くなったら、表紙（無ければ紙なし＝null）に寄せる
 */
export function rebaseEdits<H extends object, R extends WithId & { sheetId: string | null }, S extends WithId & { type: string }>(
  base: { head: H; rows: readonly R[]; sheets: readonly S[] },
  mine: { head: H; rows: readonly R[]; sheets: readonly S[] },
  theirs: { head: H; rows: readonly R[]; sheets: readonly S[] },
): { head: H; rows: R[]; sheets: S[] } {
  const sheets = rebaseList(base.sheets, mine.sheets, theirs.sheets);
  const ids = new Set(sheets.map((x) => x.id));
  const cover = sheets.find((x) => x.type === "cover")?.id ?? null;
  const rows = rebaseList(base.rows, mine.rows, theirs.rows).map((r) =>
    sheets.length ? (r.sheetId && ids.has(r.sheetId) ? r : { ...r, sheetId: cover }) : r.sheetId == null ? r : { ...r, sheetId: null },
  );
  return { head: rebaseFields(base.head, mine.head, theirs.head), rows, sheets };
}
